import { CloseButton } from "@headlessui/react";
import { Trans, t } from "@lingui/macro";
import { Edit } from "@winamp/icons/Edit.js";
import { Trash } from "@winamp/icons/Trash.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import type { Dialog } from "@winamp/ui/components/Dialog/Dialog.js";
import { DialogActions } from "@winamp/ui/components/Dialog/DialogActions.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogDescription } from "@winamp/ui/components/Dialog/DialogDescription.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { DialogWrapper } from "@winamp/ui/components/Dialog/DialogWrapper.js";
import { Input } from "@winamp/ui/components/Input/Input.js";
import { Switch } from "@winamp/ui/components/Switch/Switch.js";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { SocialMediaIcon } from "~/components/SocialMediaIcon";
import { LinksLibraryCreateDialog } from "./LinksLibraryCreateDialog";
import { LinksLibraryDeleteDialog } from "./LinksLibraryDeleteDialog";
import { LinksLibraryEditDialog } from "./LinksLibraryEditDialog";
import { useLinks } from "./useLinks";

type LinksLibraryDialogProps<TValue = Array<string>> = {
  artistUid: string;
  children: ReactNode | (({ open }: { open: () => void }) => ReactNode);
  initialIsOpen?: boolean;
  scope?: "social" | "distribution";
  value?: TValue;
  onChange?: (links: TValue) => void;
} & Omit<
  ComponentProps<typeof Dialog>,
  "children" | "open" | "onClose" | "unmount" | "onChange"
>;

export function LinksLibraryDialog({
  artistUid,
  children,
  initialIsOpen = false,
  onChange,
  scope,
  value,
  ...props
}: LinksLibraryDialogProps) {
  const { links, isLoading } = useLinks({ artistUid, scope });

  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [selection, setSelection] = useState<Array<string>>(value ?? []);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    // When the input value changes
    if (isOpen) {
      setSelection(value ?? []);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    setIsPending(true);
    await onChange?.(selection);
    setIsPending(false);
    handleClose();
  };

  const [search, setSearch] = useState<string>();
  const filteredLinks =
    search && search.length > 0
      ? links.filter((link) =>
          [link.label, link.url, link.platform?.name]
            .filter(Boolean)
            .map((label) => label.toLowerCase())
            .some((label) => label.includes(search.toLowerCase())),
        )
      : links;

  return (
    <>
      {typeof children === "function"
        ? children({ open: () => setIsOpen(!isOpen) })
        : children}
      <DialogWrapper
        {...props}
        open={isOpen}
        onClose={() => handleClose()}
        loading={isLoading}
      >
        <DialogTitle>
          <Trans>Links Library</Trans>
        </DialogTitle>
        <DialogDescription>
          <Trans>
            Your links library is where you manage all the links you use through
            Winamp for Creators. It makes it easy to reuse the same links in the
            Fanzone or in Winamp.link.
          </Trans>
        </DialogDescription>
        <DialogBody>
          <Input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={t`Search links library...`}
          />
          <div className="flex flex-col gap-5 max-h-[40vh] overflow-y-auto mt-4">
            {links.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <p className="text-sm font-medium">
                  <Trans>Library is empty</Trans>
                </p>
                <p className="text-sm text-secondary mb-4">
                  <Trans>
                    You don't have any links yet. Start by creating a new link.
                  </Trans>
                </p>
                <LinksLibraryCreateDialog
                  artistUid={artistUid}
                  onComplete={(linkUid) => {
                    setSelection((selection) =>
                      selection.includes(linkUid)
                        ? selection
                        : [...selection, linkUid],
                    );
                  }}
                >
                  {({ open }) => (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => open()}
                    >
                      <Trans>Create new Link</Trans>
                    </Button>
                  )}
                </LinksLibraryCreateDialog>
              </div>
            )}
            {links.length > 0 && filteredLinks.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <p className="text-sm font-medium">
                  <Trans>No results</Trans>
                </p>
                <p className="text-sm text-secondary mb-4">
                  <Trans>
                    You don't have any links matching your search "{search}"
                  </Trans>
                </p>
                <LinksLibraryCreateDialog
                  artistUid={artistUid}
                  onComplete={(linkUid) => {
                    setSelection((selection) =>
                      selection.includes(linkUid)
                        ? selection
                        : [...selection, linkUid],
                    );
                  }}
                >
                  {({ open }) => (
                    <Button
                      className="max-sm:w-full"
                      variant="secondary"
                      size="2xs"
                      onClick={() => open()}
                    >
                      <Trans>Create new Link</Trans>
                    </Button>
                  )}
                </LinksLibraryCreateDialog>
              </div>
            )}
            {filteredLinks.map((link) => (
              <div key={link.uid} className="flex items-center gap-2">
                <SocialMediaIcon
                  value={link.url}
                  type="url"
                  className="size-6 shrink-0"
                />
                <div className="flex flex-col overflow-hidden">
                  {link.label && (
                    <span className="text-sm/5 font-medium inline-block max-w-full truncate">
                      {link.label}
                    </span>
                  )}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="contents"
                  >
                    <span className="text-2xs leading-none break-all text-tertiary">
                      {link.url}
                    </span>
                  </a>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  {onChange && (
                    <Switch
                      checked={selection.includes(link.uid)}
                      onChange={(checked) =>
                        checked
                          ? setSelection((selection) => [
                              ...selection,
                              link.uid,
                            ])
                          : setSelection((selection) =>
                              selection.filter(
                                (selectionUid) => selectionUid !== link.uid,
                              ),
                            )
                      }
                    />
                  )}
                  <LinksLibraryEditDialog artistUid={artistUid} link={link}>
                    {({ open }) => (
                      <Button
                        icon
                        variant="ghost"
                        size="xs"
                        onClick={() => open()}
                      >
                        <Edit />
                      </Button>
                    )}
                  </LinksLibraryEditDialog>
                  <LinksLibraryDeleteDialog artistUid={artistUid} link={link}>
                    {({ open }) => (
                      <Button
                        icon
                        variant="ghost"
                        size="xs"
                        onClick={() => open()}
                      >
                        <Trash />
                      </Button>
                    )}
                  </LinksLibraryDeleteDialog>
                </div>
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <LinksLibraryCreateDialog
              artistUid={artistUid}
              onComplete={(linkUid) => {
                setSelection((selection) =>
                  selection.includes(linkUid)
                    ? selection
                    : [...selection, linkUid],
                );
              }}
            >
              {({ open }) => (
                <Button
                  className="max-sm:w-full"
                  variant="ghost"
                  onClick={() => open()}
                >
                  <Trans>Create new Link</Trans>
                </Button>
              )}
            </LinksLibraryCreateDialog>
          </div>
          {onChange ? (
            <>
              <CloseButton as={Button} variant="secondary">
                <Trans>Cancel</Trans>
              </CloseButton>
              <Button loading={isPending} onClick={() => handleSubmit()}>
                <Trans>Save Selection</Trans>
              </Button>
            </>
          ) : (
            <CloseButton as={Button}>
              <Trans>Close</Trans>
            </CloseButton>
          )}
        </DialogActions>
      </DialogWrapper>
    </>
  );
}
